import { getCountryCallingCode } from "libphonenumber-js";
import { COUNTRIES } from "./data/countriesList";

/** When several ISOs share one calling code, prefer this ISO for legacy dial → select value. */
const PREFERRED_ISO_FOR_SHARED_DIAL = {
  "+1": "US",
  "+7": "RU",
  "+44": "GB",
  "+212": "MA",
  "+262": "RE",
  "+290": "SH",
  "+47": "NO",
};

function dialForIso(iso) {
  try {
    return `+${getCountryCallingCode(iso)}`;
  } catch {
    return "+44";
  }
}

const _rows = COUNTRIES.map((c) => {
  const dialCode = dialForIso(c.value);
  return {
    value: c.value,
    dialCode,
    label: `${c.label} (${dialCode})`,
  };
});

/** @type {Map<string, string[]>} dialCode -> ISO codes (unsorted within dial) */
const DIAL_TO_ISOS = new Map();
for (const r of _rows) {
  const list = DIAL_TO_ISOS.get(r.dialCode) || [];
  list.push(r.value);
  DIAL_TO_ISOS.set(r.dialCode, list);
}

/** One row per country: `value` = ISO 3166-1 alpha-2, `dialCode` = E.164 prefix, `label` for `<option>`. */
export const COUNTRY_DIAL_OPTIONS = [..._rows].sort((a, b) => a.label.localeCompare(b.label, "en"));

const SUPPORTED_DIAL_COUNTRY_ISOS = new Set(_rows.map((r) => r.value));

/** True if `iso` is a country we list in the phone `<select>`. */
export function isDialCountryIsoSupported(iso) {
  return SUPPORTED_DIAL_COUNTRY_ISOS.has(String(iso || "").trim().toUpperCase());
}

/**
 * @param {string} iso
 * @returns {string} e.g. "+44"
 */
export function getDialCodeForCountryIso(iso) {
  const s = String(iso || "").trim().toUpperCase();
  const row = _rows.find((r) => r.value === s);
  return row?.dialCode || "+44";
}

/**
 * Map a stored dial prefix (e.g. "+44", "44") to a country ISO for `<select value>`.
 * @param {string} dial
 * @returns {string} ISO or "" if unknown
 */
export function getCountryIsoFromDial(dial) {
  const raw = String(dial || "").trim();
  if (!raw) return "";
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  const normalized = raw.startsWith("+") ? raw : `+${raw.replace(/^\+/, "")}`;
  const list = DIAL_TO_ISOS.get(normalized) || [];
  if (list.length === 0) return "";
  if (list.length === 1) return list[0];
  const pref = PREFERRED_ISO_FOR_SHARED_DIAL[normalized];
  if (pref && list.includes(pref)) return pref;
  return list[0];
}
